// src/contexts/CurrencyContext.tsx
import React, { createContext, useContext } from 'react';
import {
  CurrencyCode,
  Currency,
  CURRENCIES,
  DEFAULT_CURRENCY,
} from '../types/currency';

interface CurrencyContextType {
  currency: Currency;
  currencyCode: CurrencyCode;
  setCurrency: (code: CurrencyCode) => Promise<void>;
  formatAmount: (amount: number, showSymbol?: boolean) => string;
  loading: boolean;
}

const CurrencyContext = createContext<CurrencyContextType | undefined>(
  undefined
);

export const CurrencyProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const currencyCode: CurrencyCode = DEFAULT_CURRENCY;
  const loading = false;

  const currency = CURRENCIES[currencyCode];

  const setCurrency = async (code: CurrencyCode) => {
    if (code !== DEFAULT_CURRENCY) {
      throw new Error('EGP is the only supported platform currency');
    }
  };

  // تنسيق المبلغ مع العملة
  const formatAmount = (amount: number, showSymbol: boolean = true): string => {
    const formatted = new Intl.NumberFormat(currency.locale, {
      minimumFractionDigits: currency.decimals,
      maximumFractionDigits: currency.decimals,
    }).format(amount);

    if (!showSymbol) {
      return formatted;
    }

    if (currency.position === 'before') {
      return `${currency.symbolAr} ${formatted}`;
    } else {
      return `${formatted} ${currency.symbolAr}`;
    }
  };

  return (
    <CurrencyContext.Provider
      value={{
        currency,
        currencyCode,
        setCurrency,
        formatAmount,
        loading,
      }}
    >
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (context === undefined) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};
